/**
 * Teks bawaan tabel (MUI DataGrid) dalam bahasa Indonesia.
 *
 * MUI X versi 7 menyediakan berkas terjemahan untuk puluhan bahasa, tetapi
 * TIDAK untuk bahasa Indonesia - sehingga seluruh tabel di aplikasi ini
 * menampilkan "No rows", "Rows per page", "Filters", dan seterusnya. Daftar
 * di bawah menggantikan semuanya; kunci yang tidak disebut di sini tetap
 * memakai bawaan MUI.
 *
 * Dipakai lewat prop `localeText` pada setiap pembungkus DataGrid
 * (ClientDataGrid, CustomDataGrid, CustomDataGridClient) dan pada satu
 * pemakaian DataGrid langsung di CMS Kategori. Pemanggil masih bisa menimpa
 * satu-dua kunci dengan mengirim `localeText` sendiri.
 */
export const TEKS_TABEL_ID = {
  // Keadaan kosong
  noRowsLabel: "Tidak ada data",
  noResultsOverlayLabel: "Tidak ada data yang cocok.",

  // Kerapatan baris
  toolbarDensity: "Kerapatan",
  toolbarDensityLabel: "Kerapatan",
  toolbarDensityCompact: "Rapat",
  toolbarDensityStandard: "Standar",
  toolbarDensityComfortable: "Lega",

  // Tombol kolom
  toolbarColumns: "Kolom",
  toolbarColumnsLabel: "Pilih kolom",

  // Tombol saringan
  toolbarFilters: "Saringan",
  toolbarFiltersLabel: "Tampilkan saringan",
  toolbarFiltersTooltipHide: "Sembunyikan saringan",
  toolbarFiltersTooltipShow: "Tampilkan saringan",
  toolbarFiltersTooltipActive: (jumlah) => `${jumlah} saringan aktif`,

  // Kotak pencarian cepat
  toolbarQuickFilterPlaceholder: "Cari...",
  toolbarQuickFilterLabel: "Cari",
  toolbarQuickFilterDeleteIconLabel: "Hapus",

  // Tombol ekspor
  toolbarExport: "Ekspor",
  toolbarExportLabel: "Ekspor",
  toolbarExportCSV: "Unduh sebagai CSV",
  toolbarExportPrint: "Cetak",
  toolbarExportExcel: "Unduh sebagai Excel",

  // Panel pengaturan kolom
  columnsManagementSearchTitle: "Cari",
  columnsManagementNoColumns: "Tidak ada kolom",
  columnsManagementShowHideAllText: "Tampilkan/Sembunyikan Semua",
  columnsManagementReset: "Atur Ulang",
  columnsManagementDeleteIconLabel: "Hapus",

  // Panel saringan
  filterPanelAddFilter: "Tambah saringan",
  filterPanelRemoveAll: "Hapus semua",
  filterPanelDeleteIconLabel: "Hapus",
  filterPanelLogicOperator: "Operator logika",
  filterPanelOperator: "Operator",
  filterPanelOperatorAnd: "Dan",
  filterPanelOperatorOr: "Atau",
  filterPanelColumns: "Kolom",
  filterPanelInputLabel: "Nilai",
  filterPanelInputPlaceholder: "Nilai saringan",

  // Operator saringan
  filterOperatorContains: "mengandung",
  filterOperatorDoesNotContain: "tidak mengandung",
  filterOperatorEquals: "sama dengan",
  filterOperatorDoesNotEqual: "tidak sama dengan",
  filterOperatorStartsWith: "diawali dengan",
  filterOperatorEndsWith: "diakhiri dengan",
  filterOperatorIs: "adalah",
  filterOperatorNot: "bukan",
  filterOperatorAfter: "setelah",
  filterOperatorOnOrAfter: "pada atau setelah",
  filterOperatorBefore: "sebelum",
  filterOperatorOnOrBefore: "pada atau sebelum",
  filterOperatorIsEmpty: "kosong",
  filterOperatorIsNotEmpty: "tidak kosong",
  filterOperatorIsAnyOf: "salah satu dari",

  // Operator saringan di baris kepala kolom
  headerFilterOperatorContains: "Mengandung",
  headerFilterOperatorDoesNotContain: "Tidak mengandung",
  headerFilterOperatorEquals: "Sama dengan",
  headerFilterOperatorDoesNotEqual: "Tidak sama dengan",
  headerFilterOperatorStartsWith: "Diawali dengan",
  headerFilterOperatorEndsWith: "Diakhiri dengan",
  headerFilterOperatorIs: "Adalah",
  headerFilterOperatorNot: "Bukan",
  headerFilterOperatorAfter: "Setelah",
  headerFilterOperatorOnOrAfter: "Pada atau setelah",
  headerFilterOperatorBefore: "Sebelum",
  headerFilterOperatorOnOrBefore: "Pada atau sebelum",
  headerFilterOperatorIsEmpty: "Kosong",
  headerFilterOperatorIsNotEmpty: "Tidak kosong",
  headerFilterOperatorIsAnyOf: "Salah satu dari",
  "headerFilterOperator=": "Sama dengan",
  "headerFilterOperator!=": "Tidak sama dengan",
  "headerFilterOperator>": "Lebih besar dari",
  "headerFilterOperator>=": "Lebih besar atau sama dengan",
  "headerFilterOperator<": "Lebih kecil dari",
  "headerFilterOperator<=": "Lebih kecil atau sama dengan",

  // Nilai saringan
  filterValueAny: "apa saja",
  filterValueTrue: "ya",
  filterValueFalse: "tidak",

  // Menu pada kepala kolom
  columnMenuLabel: "Menu",
  columnMenuAriaLabel: (namaKolom) => `Menu kolom ${namaKolom}`,
  columnMenuShowColumns: "Tampilkan kolom",
  columnMenuManageColumns: "Atur kolom",
  columnMenuFilter: "Saring",
  columnMenuHideColumn: "Sembunyikan kolom",
  columnMenuUnsort: "Batalkan urutan",
  columnMenuSortAsc: "Urutkan menaik",
  columnMenuSortDesc: "Urutkan menurun",

  // Kepala kolom
  columnHeaderFiltersTooltipActive: (jumlah) => `${jumlah} saringan aktif`,
  columnHeaderFiltersLabel: "Tampilkan saringan",
  columnHeaderSortIconLabel: "Urutkan",

  // Kaki tabel
  footerRowSelected: (jumlah) => `${jumlah.toLocaleString("id-ID")} baris dipilih`,
  footerTotalRows: "Jumlah Baris:",
  footerTotalVisibleRows: (terlihat, total) =>
    `${terlihat.toLocaleString("id-ID")} dari ${total.toLocaleString("id-ID")}`,

  // Kotak centang pemilihan baris
  checkboxSelectionHeaderName: "Pemilihan baris",
  checkboxSelectionSelectAllRows: "Pilih semua baris",
  checkboxSelectionUnselectAllRows: "Batalkan pilihan semua baris",
  checkboxSelectionSelectRow: "Pilih baris",
  checkboxSelectionUnselectRow: "Batalkan pilihan baris",

  // Sel bertipe benar/salah
  booleanCellTrueLabel: "ya",
  booleanCellFalseLabel: "tidak",

  // Sel aksi
  actionsCellMore: "lainnya",

  // Menyematkan kolom
  pinToLeft: "Sematkan di kiri",
  pinToRight: "Sematkan di kanan",
  unpin: "Lepas sematan",

  // Data bertingkat
  treeDataGroupingHeaderName: "Kelompok",
  treeDataExpand: "tampilkan turunan",
  treeDataCollapse: "sembunyikan turunan",

  // Pengelompokan kolom
  groupingColumnHeaderName: "Kelompok",
  groupColumn: (nama) => `Kelompokkan berdasarkan ${nama}`,
  unGroupColumn: (nama) => `Berhenti mengelompokkan berdasarkan ${nama}`,

  // Panel rincian
  detailPanelToggle: "Buka/tutup panel rincian",
  expandDetailPanel: "Buka",
  collapseDetailPanel: "Tutup",

  // Penataan ulang baris
  rowReorderingHeaderName: "Penataan ulang baris",

  // Agregasi
  aggregationMenuItemHeader: "Agregasi",
  aggregationFunctionLabelSum: "jumlah",
  aggregationFunctionLabelAvg: "rata-rata",
  aggregationFunctionLabelMin: "terkecil",
  aggregationFunctionLabelMax: "terbesar",
  aggregationFunctionLabelSize: "banyaknya",

  /*
   * Bagian pagination datang dari MUI Material, bukan dari DataGrid, sehingga
   * terjemahannya harus dititipkan lewat kunci khusus ini - kalau tidak,
   * "Rows per page" dan "1-10 of 25" tetap berbahasa Inggris.
   */
  MuiTablePagination: {
    labelRowsPerPage: "Baris per halaman:",
    labelDisplayedRows: ({ from, to, count }) =>
      `${from}-${to} dari ${count !== -1 ? count : `lebih dari ${to}`}`,
    getItemAriaLabel: (jenis) => {
      if (jenis === "first") return "Ke halaman pertama";
      if (jenis === "last") return "Ke halaman terakhir";
      if (jenis === "next") return "Ke halaman berikutnya";
      return "Ke halaman sebelumnya";
    },
  },
};

/** Label tombol buatan sendiri di baris alat tabel. */
export const LABEL_SESUAIKAN_KOLOM = "Sesuaikan Lebar Kolom";
